import React from 'react';
import { ArrowLeft, MapPin, Calendar, Camera, Zap, Users, Mic2, Mail, LayoutGrid } from 'lucide-react';

interface ArchiveSectionProps {
  onBack?: () => void;
}

const ArchiveSection: React.FC<ArchiveSectionProps> = ({ onBack }) => {
  /**
   * VERCEL BLOB STORAGE:
   * Using the provided Montage image from Vercel Blob.
   */
  const MAIN_RECAP = "https://dftzmoroigefgx46.public.blob.vercel-storage.com/images/Montage.png";
  
  // Gallery grid placeholders for future expansion
  const GALLERY_IMAGES = [
    { url: "/salon-01-01.jpg", alt: "Salon I Discussion" },
    { url: "/salon-01-02.jpg", alt: "Salon I Workshop" },
    { url: "/salon-01-03.jpg", alt: "Salon I Connection" },
  ];

  const PLACEHOLDER = "https://images.unsplash.com/photo-1515187029135-18ee286d815b?auto=format&fit=crop&q=80&w=1200";

  return (
    <section className="min-h-screen py-32 md:py-48 bg-white text-charcoal px-6 relative overflow-hidden">
      <div className="max-w-6xl mx-auto relative z-10">
        {onBack && (
          <button 
            onClick={onBack}
            className="mb-12 flex items-center gap-2 text-neutral-400 hover:text-terracotta transition-colors group"
          >
            <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
            <span className="text-[10px] font-black uppercase tracking-[0.3em]">Return Home</span>
          </button>
        )}

        <div className="space-y-16">
          <div className="space-y-6">
            <span className="text-terracotta text-[10px] font-black uppercase tracking-[0.5em] block">The Record</span>
            <h1 className="text-6xl md:text-9xl leading-[0.85] tracking-tighter font-black text-charcoal italic">
              Salon I: <br/>
              <span className="text-neutral-400 not-italic">More Human.</span>
            </h1>
            <div className="flex flex-wrap gap-8 pt-4">
              <div className="flex items-center gap-2 text-neutral-400">
                <Calendar size={14} />
                <span className="text-[10px] font-black uppercase tracking-widest text-neutral-800">January 27, 2026</span>
              </div>
              <div className="flex items-center gap-2 text-neutral-400">
                <MapPin size={14} />
                <span className="text-[10px] font-black uppercase tracking-widest text-neutral-800">Benedictine University</span>
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-12 gap-16">
            {/* Recap Content */}
            <div className="lg:col-span-7 space-y-16">
              <div className="relative p-1 bg-cream rounded-xl overflow-hidden shadow-lg transform -rotate-1 group hover:rotate-0 transition-transform">
                <img 
                  src={MAIN_RECAP} 
                  onError={(e) => { (e.target as HTMLImageElement).src = PLACEHOLDER; }}
                  alt="Salon I Recap Visual Montage" 
                  className="w-full h-auto grayscale rounded-lg"
                />
                <div className="absolute top-4 right-4 bg-white/90 backdrop-blur px-3 py-1 rounded text-[8px] font-black uppercase tracking-widest">Archive Record #001</div>
              </div>

              <div className="space-y-8 text-xl text-neutral-800 leading-relaxed font-normal">
                <p>
                  Our inaugural gathering was a proof of concept. In a room overlooking Lisle, we moved past the technical "how" of AI to the existential "why." 
                </p>
                <div className="border-l-4 border-terracotta pl-10 py-4 my-10">
                   <p className="text-2xl font-black text-charcoal italic leading-tight mb-4">
                    "AI doesn't fail because it's too fast. It fails when meaning collapses."
                   </p>
                   <span className="text-[10px] font-black uppercase tracking-widest text-terracotta">— Key Theme: The Meaning Shift</span>
                </div>
                <p>
                  The evening followed a three-act structure designed to ground participants in the human element of technology. We explored how judgment is the new primary currency in an automated age.
                </p>
              </div>

              {/* Gallery Grid */}
              <div className="space-y-8 pt-10 border-t border-neutral-100">
                <div className="flex items-center gap-3">
                  <LayoutGrid size={16} className="text-terracotta" />
                  <h3 className="text-[10px] font-black uppercase tracking-[0.4em] text-neutral-400">The Visual Narrative</h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {GALLERY_IMAGES.map((img, i) => (
                    <div key={i} className="aspect-square bg-neutral-50 rounded-lg overflow-hidden relative group">
                      <img 
                        src={img.url} 
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        alt={img.alt}
                        className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-500"
                      />
                      {/* Empty state for gallery placeholders */}
                      <div className="absolute inset-0 flex items-center justify-center text-[8px] font-black uppercase tracking-tighter text-neutral-200 pointer-events-none group-hover:text-terracotta/20">
                        {img.url.split('/').pop()}
                      </div>
                    </div>
                  ))}
                  <div className="aspect-square bg-neutral-50 rounded-lg border-2 border-dashed border-neutral-100 flex flex-col items-center justify-center p-4 text-center">
                    <Camera size={20} className="text-neutral-200 mb-2" />
                    <span className="text-[8px] font-black uppercase tracking-widest text-neutral-300 leading-tight">More visuals<br/>coming soon</span>
                  </div>
                </div>
              </div>

              {/* Speaker Section */}
              <div className="space-y-10 pt-10 border-t border-neutral-100">
                <div className="flex items-center gap-3">
                  <Mic2 size={16} className="text-terracotta" />
                  <h3 className="text-[10px] font-black uppercase tracking-[0.4em] text-neutral-400">The Voices of Salon I</h3>
                </div>
                
                <div className="grid md:grid-cols-3 gap-10">
                  <div className="space-y-4">
                    <h4 className="text-xl font-black text-charcoal leading-tight">Arthur Zards</h4>
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-terracotta">Founder & Curator</p>
                    <p className="text-xs text-neutral-800 leading-relaxed font-medium">
                      TEDxNaperville founder and veteran curator. Arthur focuses on the vital intersection of human agency and machine intelligence.
                    </p>
                  </div>
                  <div className="space-y-4">
                    <h4 className="text-xl font-black text-charcoal leading-tight">Dr. Philip Hardy</h4>
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-terracotta">Innovation Leader</p>
                    <p className="text-xs text-neutral-800 leading-relaxed font-medium">
                      Director of Innovation at Benedictine University. An expert in navigating institutional evolution and building cross-sector partnerships.
                    </p>
                  </div>
                  <div className="space-y-4">
                    <h4 className="text-xl font-black text-charcoal leading-tight">Jose DeFrancisco</h4>
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-terracotta">Design Innovator</p>
                    <p className="text-xs text-neutral-800 leading-relaxed font-medium">
                      Bell Labs Distinguished Member. A pioneer in Human-Scale AI, bridging the gap between startups and global enterprise.
                    </p>
                    <a href="mailto:jdf@innovarista.com" className="inline-flex items-center gap-2 text-[9px] font-black uppercase tracking-widest text-neutral-400 hover:text-terracotta transition-colors">
                      <Mail size={10} />
                      Connect
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar / Outcomes */}
            <div className="lg:col-span-5 space-y-10 lg:sticky lg:top-32">
              <div className="bg-[#1A1A1A] text-white p-10 rounded-2xl shadow-xl space-y-8 border border-white/5">
                <h3 className="text-2xl font-black tracking-tight">Post-Salon Signal</h3>
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <Zap size={18} className="text-terracotta shrink-0 mt-1" />
                    <p className="text-sm font-medium text-neutral-100">Participants reported an increased focus on <span className="text-terracotta font-bold italic">Judgment</span> over sheer output.</p>
                  </div>
                  <div className="flex gap-4">
                    <Users size={18} className="text-terracotta shrink-0 mt-1" />
                    <p className="text-sm font-medium text-neutral-100">A new network formed between local tech leaders and creative educators.</p>
                  </div>
                </div>
                <div className="pt-6 border-t border-white/10">
                  <p className="text-[10px] font-black uppercase tracking-[0.4em] text-neutral-400">Feedback Loop</p>
                  <p className="mt-4 text-white text-lg font-bold italic leading-tight">"I finally understand why I've been feeling anxious—it's not the tech, it's the lack of orientation."</p>
                </div>
              </div>

              <div className="p-10 border border-neutral-100 rounded-2xl space-y-6 bg-cream/20">
                <h4 className="text-[10px] font-black uppercase tracking-[0.4em] text-neutral-400">The Context</h4>
                <div className="space-y-4 text-xs text-neutral-600 leading-relaxed font-medium">
                  <p>Held in the Goodwin Hall of Business, Salon I brought together 50+ diverse thinkers from across the Midwest.</p>
                  <p>The goal wasn't a technical demo, but a collective orientation session for the new reality of co-intelligence.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute -bottom-20 -right-20 text-[18rem] font-black text-neutral-900/[0.03] select-none pointer-events-none leading-none rotate-3">
        RECORD
      </div>
    </section>
  );
};

export default ArchiveSection;